import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  alpha,
  useTheme,
  Tooltip,
  Chip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout,
  Settings,
  Home,
  QuestionAnswer,
  Help
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Header = ({ onMenuToggle, isMobile, isLandingPage = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, signOut, currentPortal, openAuthModal, getUserCategory } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    signOut();
    handleClose();
  };

  const handleProfileClick = () => {
    navigate('/profile');
    handleClose();
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleHelpClick = () => {
    navigate('/help');
  };

  const handleMammoAssistClick = () => {
    const category = getUserCategory ? getUserCategory() : null;
    if (category === 'trials_team') {
      navigate('/manager-review');
      return;
    }
    navigate('/dashboard');
  };

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };

  const getPortalName = () => {
    switch (currentPortal) {
      case 'patient': return 'Patient Portal';
      case 'trials': return 'Clinical Trials Portal';
      case 'community': return 'Community Portal';
      default: return 'MammoTrials';
    }
  };

  const getUserInitials = () => {
    const firstName = user?.first_name || '';
    const lastName = user?.last_name || '';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 
           user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        {/* Menu Button for Mobile */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={onMenuToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={handleHomeClick}
          >
            MammoTrials
          </Typography>
          {user && (
            <Chip
              label={getPortalName()}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ 
                ml: 2,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                display: { xs: 'none', sm: 'block' },
                '&:hover': { borderColor: 'rgba(255,255,255,0.5)' }
              }}
            />
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Right Side Icons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!user ? (
            /* Not Authenticated: Sign In Primary Button */
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                onClick={() => openAuthModal('signin')}
                variant="contained"
                size="small"
                sx={{ 
                  bgcolor: 'white', 
                  color: 'primary.main',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                Sign In
              </Button>
              <Button 
                onClick={() => openAuthModal('signup')}
                variant="text"
                size="small"
                sx={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  textTransform: 'none',
                  fontWeight: 500,
                  textDecoration: 'underline',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline',
                    bgcolor: 'rgba(255,255,255,0.1)' 
                  }
                }}
              >
                Sign Up
              </Button>
            </Box>
          ) : (
            /* Authenticated User: Full Header with Icons and Profile */
            <>
              {/* Home/Dashboard Quick Access */}
              <Tooltip title="Dashboard">
                <IconButton 
                  color="inherit" 
                  size="large"
                  onClick={handleHomeClick}
                >
                  <Home />
                </IconButton>
              </Tooltip>

              {/* MammoAssist AI Quick Access */}
              <Tooltip title="MammoAssist AI">
                <IconButton color="inherit" size="large" onClick={handleMammoAssistClick} aria-label="MammoAssist AI">
                  <QuestionAnswer />
                </IconButton>
              </Tooltip>

          {/* Help/Support */}
          <Tooltip title="Help & Support">
            <IconButton 
              color="inherit" 
              size="large"
              onClick={handleHelpClick}
            >
              <Help />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              color="inherit"
              size="large"
              onClick={handleNotificationsClick}
              aria-label="Notifications"
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Profile Avatar */}
          <Tooltip title={`${user?.first_name} ${user?.last_name} - Click for Profile`}>
            <IconButton
              color="inherit"
              size="large"
              onClick={handleProfileMenu}
              sx={{ 
                ml: 1,
                '&:hover': { 
                  backgroundColor: alpha(theme.palette.common.white, 0.1) 
                }
              }}
            >
              <Avatar sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: 'secondary.main',
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}>
                {getUserInitials()}
              </Avatar>
            </IconButton>
          </Tooltip>
          </>
          )}
        </Box>

        {/* Profile Menu - only for authenticated users */}
        {user && (
          <>
            {/* Profile Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  '& .MuiMenuItem-root': {
                    px: 2,
                    py: 1
                  }
                }
              }}
            >
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              
              <MenuItem onClick={handleProfileClick}>
                <AccountCircle sx={{ mr: 2 }} />
                View Profile
              </MenuItem>
              
              <MenuItem onClick={() => { navigate('/settings'); handleClose(); }}>
                <Settings sx={{ mr: 2 }} />
                Settings
              </MenuItem>
              
              <MenuItem onClick={() => { navigate('/help'); handleClose(); }}>
                <Help sx={{ mr: 2 }} />
                Help & Support
              </MenuItem>
              
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <Logout sx={{ mr: 2 }} />
                Sign Out
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;